import type { UseInfiniteQueryOptions, UseQueryOptions } from "@tanstack/react-query";
import type { AxiosError } from "axios";
import { useCallback, useMemo } from "react";
import {
  useInfiniteQuery,
  useQueries,
  useQuery,
} from "@tanstack/react-query";
import { KEYS, repository } from "@repositories/Repository";
import type ISoftwareRepository from "@repositories/softwareRepository/ISoftwareRepository";
import type {
  Filters,
  ProductPlan,
  ProductPlanListDTO,
  Software,
  SoftwareSimple,
  UsageMetric,
} from "@repositories/softwareRepository/software.types";
import type { Page } from "@type/Page";
import type { UseQueryOptionsType } from "./UseQueryOptionsType";
import { ProductPlanListModel } from "@repositories/softwareRepository/models/ProductPlanListModel";
import { SoftwareModel } from "@repositories/softwareRepository/models/SoftwareModel";
import { SoftwareSimpleModel } from "@repositories/softwareRepository/models/SoftwareSimpleModel";
import { ProductPlanModel } from "@repositories/softwareRepository/models/ProductPlanModel";
import { UsageMetricModel } from "@repositories/softwareRepository/models/UsageMetricModel";

const swRepo = repository.get<ISoftwareRepository>(KEYS.SOFTWARE_REPOSITORY);
export const swQueryKey = {
  all: ["software"] as const,
  lists: () => [...swQueryKey.all, "list"] as const,
  one: (softwareId: SoftwareId) => [...swQueryKey.all, softwareId] as const,
  list: (filter: Filters) => [...swQueryKey.lists(), { ...filter }] as const,
  listInfinite: (filter: Filters) =>
    [...swQueryKey.list(filter), "infinite"] as const,
  planList: (softwareId: SoftwareId) => [...swQueryKey.one(softwareId), "plan"],
  plan: (softwareId: SoftwareId, planId: string) =>
    [...swQueryKey.planList(softwareId), planId] as const,
  usageMetricList: (softwareId: SoftwareId) => [
    ...swQueryKey.one(softwareId),
    "usageMetric",
  ],
};

export const useGetSoftwareList = (
  filter: Filters,
  options?: Omit<UseQueryOptions<Page<SoftwareSimple>, AxiosError, Page<SoftwareSimpleModel>>, 'queryKey' | 'queryFn' | 'select'>,
) => {
  const selectSoftwareList = useCallback(
    (data: Page<SoftwareSimple>) => ({
      ...data,
      content: data.content.map((dto) => new SoftwareSimpleModel(dto)),
    }),
    [],
  );

  const result = useQuery<Page<SoftwareSimple>, AxiosError, Page<SoftwareSimpleModel>>({
    queryKey: swQueryKey.list(filter),
    queryFn: () => swRepo.getList(filter),
    select: selectSoftwareList,
    ...options,
  });

  const initialData = useMemo(
    () => ({ content: [] as SoftwareSimpleModel[], totalElements: 0 }),
    []
  );
  return { ...result, data: result.data ?? initialData };
};

export const useGetSoftwareListInfinite = (
  filter: Omit<Filters, "page">,
  options?: UseInfiniteQueryOptions<Page<SoftwareSimpleModel>>
) =>
  useInfiniteQuery({
    queryKey: swQueryKey.listInfinite(filter),
    queryFn: async ({ pageParam = 0 }) => {
      const result = await swRepo.getList({ ...filter, page: pageParam });
      return {
        ...result,
        content: result.content.map((dto) => new SoftwareSimpleModel(dto)),
        pageParam,
      };
    },
    ...options,
    getNextPageParam: (lastPage) =>
      lastPage.last ? undefined : lastPage.number + 1,
  });

export const useGetSoftware = (
  softwareId: SoftwareId,
  options?: Omit<UseQueryOptions<Software, AxiosError, SoftwareModel>, 'queryKey' | 'queryFn' | 'select'>,
) => {
  const selectSoftware = useCallback(
    (data: Software) => new SoftwareModel(data),
    [],
  );

  const result = useQuery<Software, AxiosError, SoftwareModel>({
    queryKey: swQueryKey.one(softwareId),
    queryFn: () => swRepo.getOne(softwareId),
    select: selectSoftware,
    ...options,
    enabled:
      !!softwareId &&
      (options?.enabled !== undefined ? options.enabled : true),
  });

  const initialData = useMemo(() => new SoftwareModel(), []);
  return { ...result, data: result.data ?? initialData };
};

export const useGetProductPlanList = (
  swId: string,
  options?: Omit<UseQueryOptions<ProductPlanListDTO[], AxiosError, ProductPlanListModel[]>, 'queryKey' | 'queryFn' | 'select'>,
) => {
  const selectProductPlanList = useCallback(
    (data: ProductPlanListDTO[]) =>
      data.map((dto) => new ProductPlanListModel(dto)),
    [],
  );

  return useQuery<ProductPlanListDTO[], AxiosError, ProductPlanListModel[]>({
    queryKey: swQueryKey.planList(swId),
    queryFn: async () => {
      const result = await swRepo.getPlanList(swId);
      result.sort((a, b) => {
        return (
          (a.planGrade ? a.planGrade : 0) - (b.planGrade ? b.planGrade : 0)
        );
      });
      return result;
    },
    select: selectProductPlanList,
    ...options,
  });
};

export const useGetProductPlanDetail = (
  swId: string,
  productPlanId: string,
  options?: Omit<UseQueryOptions<ProductPlan, AxiosError, ProductPlanModel>, 'queryKey' | 'queryFn' | 'select'>,
) => {
  const selectProductPlan = useCallback(
    (data: ProductPlan) => new ProductPlanModel(data),
    [],
  );

  return useQuery<ProductPlan, AxiosError, ProductPlanModel>({
    queryKey: swQueryKey.plan(swId, productPlanId),
    queryFn: () => swRepo.getPlanDetail(swId, productPlanId),
    select: selectProductPlan,
    ...options,
  });
};

export const useGetProductPlanDetails = (
  swId: string,
  productPlanIds: string[],
  options?: UseQueryOptionsType<ProductPlanModel>
) =>
  useQueries({
    queries: productPlanIds.map((productPlanId) => ({
      queryKey: swQueryKey.plan(swId, productPlanId),
      queryFn: async () => {
        const result = await swRepo.getPlanDetail(swId, productPlanId);
        return new ProductPlanModel(result);
      },
      ...options,
    })),
  });

export const useGetUsageMetricList = (
  swId: string,
  options?: Omit<UseQueryOptions<UsageMetric[], AxiosError, UsageMetricModel[]>, 'queryKey' | 'queryFn' | 'select'>,
) => {
  const selectUsageMetricList = useCallback(
    (data: UsageMetric[]) =>
      data.map((dto) => new UsageMetricModel(swId, dto)),
    [swId],
  );

  return useQuery<UsageMetric[], AxiosError, UsageMetricModel[]>({
    queryKey: swQueryKey.usageMetricList(swId),
    queryFn: () => swRepo.getUsageMetricList(swId),
    select: selectUsageMetricList,
    ...options,
    enabled:
      !!swId && (options?.enabled !== undefined ? !!options.enabled : true),
  });
};

export const useGetUsageMetricLists = (
  swIds: string[],
  options?: UseQueryOptionsType<UsageMetricModel[]>
) =>
  useQueries({
    queries: swIds.map((swId) => ({
      queryKey: swQueryKey.usageMetricList(swId),
      queryFn: async () => {
        const result = await swRepo.getUsageMetricList(swId);
        return result.map((dto) => new UsageMetricModel(swId, dto));
      },
      ...options,
      enabled:
        !!swId && (options?.enabled !== undefined ? !!options.enabled : true),
    })),
  });
